import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, RotateCcw, Sparkles, X, ChevronRight } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const demoSteps = [
  {
    id: 'hero',
    targetId: 'hero',
    title: "Page d'accueil",
    description: "Un message clair et impactant pour les 18-30 ans",
    highlight: '[data-testid="hero-cta-primary"]',
    duration: 4000,
    action: 'highlight-button'
  },
  {
    id: 'problem',
    targetId: 'problem',
    title: "Le problème",
    description: "67% des jeunes perdent de l'argent. On leur montre les chiffres.",
    highlight: '[data-testid="problem-section"]',
    duration: 5000,
    action: 'highlight-cards'
  },
  {
    id: 'solution',
    targetId: 'solution',
    title: "La solution Skipya",
    description: "5 fonctionnalités clés dans une seule app",
    highlight: '[data-testid="solution-section"]',
    duration: 5000,
    action: 'highlight-features'
  },
  {
    id: 'how-it-works',
    targetId: 'how-it-works',
    title: "Comment ça marche",
    description: "3 étapes simples. 60 secondes. C'est tout.",
    highlight: '[data-testid="how-it-works-section"]',
    duration: 4000,
    action: 'scroll-steps'
  },
  {
    id: 'technology',
    targetId: 'technology',
    title: "Technologie",
    description: "Basé sur des sources officielles et barèmes publics : Service-Public, OpenFisca, CAF, impots.gouv.fr",
    highlight: '[data-testid="technology-section"]',
    duration: 4000,
    action: 'highlight-partners'
  },
  {
    id: 'comparison',
    targetId: 'comparison',
    title: "Comparaison concurrents",
    description: "Le seul tout-en-un avec IA en France",
    highlight: '[data-testid="comparison-section"]',
    duration: 5000,
    action: 'highlight-table'
  },
  {
    id: 'social-proof',
    targetId: 'social-proof',
    title: "Preuve sociale",
    description: "Déjà 2 847 inscrits sur la liste d'attente",
    highlight: '[data-testid="social-proof-section"]',
    duration: 4000,
    action: 'animate-counter'
  },
  {
    id: 'cta',
    targetId: 'cta',
    title: "Call-to-action",
    description: "Conversion avec formulaire email simplifié",
    highlight: '[data-testid="email-input"]',
    duration: 4000,
    action: 'simulate-typing'
  }
];

export const DemoMode = () => {
  const { isDark } = useTheme();
  const [isActive, setIsActive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const scrollToSection = useCallback((sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  const highlightElement = useCallback((selector) => {
    // Remove previous highlights
    document.querySelectorAll('.demo-highlight').forEach(el => {
      el.classList.remove('demo-highlight');
    });
    
    if (selector) {
      const element = document.querySelector(selector);
      if (element) {
        element.classList.add('demo-highlight');
        const rect = element.getBoundingClientRect();
        setTooltipPosition({
          x: rect.left + rect.width / 2,
          y: rect.top - 20
        });
      }
    }
  }, []);

  const simulateTyping = useCallback(async () => {
    const input = document.querySelector('[data-testid="email-input"]');
    if (input) {
      const text = 'investisseur@example.com';
      input.focus();
      for (let i = 0; i <= text.length; i++) {
        await new Promise(resolve => setTimeout(resolve, 80));
        input.value = text.substring(0, i);
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  }, []);

  useEffect(() => {
    if (!isActive || isPaused) return;

    const step = demoSteps[currentStep];
    
    // Scroll to section
    scrollToSection(step.targetId);
    
    // Wait for scroll then show tooltip
    const tooltipTimer = setTimeout(() => {
      setShowTooltip(true);
      highlightElement(step.highlight);
      
      // Special actions
      if (step.action === 'simulate-typing') {
        setTimeout(simulateTyping, 500);
      }
    }, 800);

    // Move to next step
    const nextTimer = setTimeout(() => {
      setShowTooltip(false);
      highlightElement(null);
      
      if (currentStep < demoSteps.length - 1) {
        setCurrentStep(prev => prev + 1);
      } else {
        // Demo complete - restart or stop
        setCurrentStep(0);
        scrollToSection('hero');
      }
    }, step.duration);

    return () => {
      clearTimeout(tooltipTimer);
      clearTimeout(nextTimer);
    };
  }, [isActive, isPaused, currentStep, scrollToSection, highlightElement, simulateTyping]);

  const startDemo = () => {
    setIsActive(true);
    setIsPaused(false);
    setCurrentStep(0);
    scrollToSection('hero');
  };

  const stopDemo = () => {
    setIsActive(false);
    setIsPaused(false);
    setCurrentStep(0);
    setShowTooltip(false);
    highlightElement(null);
    // Clear any input values
    const input = document.querySelector('[data-testid="email-input"]');
    if (input) input.value = '';
  };

  const togglePause = () => {
    setIsPaused(prev => !prev);
  };

  const currentStepData = demoSteps[currentStep];

  return (
    <>
      {/* Demo highlight styles */}
      <style>{`
        .demo-highlight {
          position: relative;
          z-index: 100;
          animation: demo-pulse 2s ease-in-out infinite;
          box-shadow: 0 0 0 4px rgba(129, 216, 208, 0.5), 0 0 30px rgba(129, 216, 208, 0.3) !important;
          border-radius: 12px;
        }
        
        @keyframes demo-pulse {
          0%, 100% { box-shadow: 0 0 0 4px rgba(129, 216, 208, 0.5), 0 0 30px rgba(129, 216, 208, 0.3); }
          50% { box-shadow: 0 0 0 8px rgba(129, 216, 208, 0.3), 0 0 50px rgba(129, 216, 208, 0.4); }
        }

        .demo-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.3);
          z-index: 90;
          pointer-events: none;
        }
      `}</style>

      {/* Demo overlay when active */}
      <AnimatePresence>
        {isActive && (
          <motion.div 
            className="demo-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
        )}
      </AnimatePresence>

      {/* Floating demo control */}
      <motion.div
        className={`fixed bottom-6 right-6 z-[200] ${isActive ? 'bottom-24' : ''}`}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 2, type: 'spring' }}
      >
        {!isActive ? (
          <motion.button
            onClick={startDemo}
            className={`flex items-center gap-2 px-5 py-3 rounded-full font-semibold shadow-2xl transition-all duration-300 ${
              isDark 
                ? 'bg-gradient-to-r from-[#81d8d0] to-[#4ECDC4] text-[#0F0F1A] hover:shadow-[#81d8d0]/30' 
                : 'bg-gradient-to-r from-[#81d8d0] to-[#4ECDC4] text-[#0F0F1A] hover:shadow-[#81d8d0]/40'
            }`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            data-testid="demo-start-btn"
          >
            <Sparkles size={18} />
            Mode Démo
          </motion.button>
        ) : (
          <motion.div
            className={`flex items-center gap-2 p-2 rounded-full shadow-2xl ${
              isDark ? 'bg-[#1A1A2E] border border-white/10' : 'bg-white border border-gray-200'
            }`}
            initial={{ width: 'auto' }}
          >
            <button
              onClick={togglePause}
              className={`p-3 rounded-full transition-colors ${
                isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'
              }`}
              title={isPaused ? 'Reprendre' : 'Pause'}
            >
              {isPaused ? (
                <Play size={20} className="text-[#81d8d0]" />
              ) : (
                <Pause size={20} className="text-[#81d8d0]" />
              )}
            </button>
            
            <button
              onClick={() => { setCurrentStep(0); scrollToSection('hero'); }}
              className={`p-3 rounded-full transition-colors ${
                isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'
              }`}
              title="Recommencer"
            >
              <RotateCcw size={20} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
            </button>
            
            <button
              onClick={stopDemo}
              className={`p-3 rounded-full transition-colors ${
                isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'
              }`}
              title="Arrêter"
            >
              <X size={20} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
            </button>
          </motion.div>
        )}
      </motion.div>

      {/* Step indicator */}
      <AnimatePresence>
        {isActive && (
          <motion.div
            className={`fixed bottom-6 left-6 right-24 z-[200] p-4 rounded-2xl shadow-2xl ${
              isDark ? 'bg-[#1A1A2E] border border-white/10' : 'bg-white border border-gray-200'
            }`}
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Sparkles size={16} className="text-[#81d8d0]" />
                <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Mode Démo
                </span>
                {isPaused && (
                  <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-500 text-xs rounded-full">
                    En pause
                  </span>
                )}
              </div>
              <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                {currentStep + 1} / {demoSteps.length}
              </span>
            </div>
            
            {/* Progress bar */}
            <div className={`h-1 rounded-full mb-3 ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}>
              <motion.div
                className="h-full bg-gradient-to-r from-[#81d8d0] to-[#4ECDC4] rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${((currentStep + 1) / demoSteps.length) * 100}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
            
            {/* Current step info */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#81d8d0]/20 flex items-center justify-center">
                <span className="text-[#81d8d0] font-semibold">{currentStep + 1}</span>
              </div>
              <div className="flex-1">
                <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {currentStepData?.title}
                </h4>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {currentStepData?.description}
                </p>
              </div>
              <ChevronRight size={20} className={isDark ? 'text-gray-600' : 'text-gray-300'} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating tooltip near highlighted element */}
      <AnimatePresence>
        {isActive && showTooltip && tooltipPosition.y > 100 && (
          <motion.div
            className={`fixed z-[150] px-4 py-2 rounded-xl shadow-xl pointer-events-none ${
              isDark ? 'bg-[#81d8d0] text-[#0F0F1A]' : 'bg-[#81d8d0] text-[#0F0F1A]'
            }`}
            style={{
              left: tooltipPosition.x,
              top: tooltipPosition.y,
              transform: 'translate(-50%, -100%)'
            }}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
          >
            <span className="font-medium text-sm">👆 {currentStepData?.title}</span>
            <div 
              className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-8 border-r-8 border-t-8 border-transparent border-t-[#81d8d0]"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default DemoMode;
